import * as React from "react"
import { cn } from "@/lib/utils"

export interface Choice {
  /**
   * Unique identifier for the choice
   */
  id: string
  /**
   * Display label for the choice
   */
  label: string
  /**
   * Whether this choice is initially selected
   * @default false
   */
  defaultChecked?: boolean
}

export interface MultiChoiceModalProps {
  /**
   * The message to display in the modal
   */
  message: string
  /**
   * Array of choices to display
   */
  choices: Choice[]
  /**
   * Selection mode - single choice (radio) or multiple choice (checkbox)
   * @default "multiple"
   */
  mode?: 'single' | 'multiple'
  /**
   * Default selected choice ID for single-choice mode
   * Ignored in multiple-choice mode (use Choice.defaultChecked instead)
   */
  defaultSelectedId?: string
  /**
   * Text for the cancel button
   * @default "CANCEL"
   */
  cancelText?: string
  /**
   * Text for the confirm button
   * @default "CONFIRM"
   */
  confirmText?: string
  /**
   * Callback when cancel button is clicked
   */
  onCancel: () => void
  /**
   * Callback when confirm button is clicked
   * Returns array of selected choice IDs (single item for single-choice mode)
   */
  onConfirm: (selectedChoiceIds: string[]) => void
  /**
   * Optional callback when selection changes (for dynamic UI updates)
   * Returns array of currently selected choice IDs
   */
  onChange?: (selectedChoiceIds: string[]) => void
  /**
   * Additional CSS classes for the modal container
   */
  className?: string
}

/**
 * Multi-choice modal component
 *
 * A confirmation dialog with multiple checkboxes for user selection.
 * The onConfirm callback returns an array of selected choice IDs.
 * Follows ANYW3AR Figma design specifications (node-id: 683:7246).
 *
 * @example
 * ```tsx
 * <MultiChoiceModal
 *   message="Do you really want to unpair this item?"
 *   choices={[
 *     { id: 'lost', label: 'Lost', defaultChecked: true },
 *     { id: 'stolen', label: 'Stolen' },
 *     { id: 'sold', label: 'Sold' },
 *     { id: 'unused', label: 'Unused' }
 *   ]}
 *   cancelText="CANCEL"
 *   confirmText="UNPAIR"
 *   onCancel={() => console.log('cancelled')}
 *   onConfirm={(selected) => console.log('Selected:', selected)}
 * />
 * ```
 */
export const MultiChoiceModal = React.forwardRef<HTMLDivElement, MultiChoiceModalProps>(
  (
    {
      message,
      choices,
      mode = 'multiple',
      defaultSelectedId,
      cancelText = "CANCEL",
      confirmText = "CONFIRM",
      onCancel,
      onConfirm,
      onChange,
      className,
    },
    ref
  ) => {
    // Track selected choices for multi-choice mode
    const [selectedIds, setSelectedIds] = React.useState<Set<string>>(() => {
      return new Set(choices.filter((c) => c.defaultChecked).map((c) => c.id))
    })

    // Track selected choice for single-choice mode
    const [selectedId, setSelectedId] = React.useState<string | null>(() => {
      if (mode === 'single') {
        return defaultSelectedId || choices[0]?.id || null
      }
      return null
    })

    const handleCheckboxChange = (choiceId: string) => {
      setSelectedIds((prev) => {
        const newSet = new Set(prev)
        if (newSet.has(choiceId)) {
          newSet.delete(choiceId)
        } else {
          newSet.add(choiceId)
        }
        return newSet
      })
    }

    const handleRadioChange = (choiceId: string) => {
      setSelectedId(choiceId)
      onChange?.([choiceId]) // Notify parent of selection change
    }

    const handleConfirm = () => {
      if (mode === 'single') {
        onConfirm(selectedId ? [selectedId] : [])
      } else {
        onConfirm(Array.from(selectedIds))
      }
    }

    // Arrange choices in rows of 2
    const rows: Choice[][] = []
    for (let i = 0; i < choices.length; i += 2) {
      rows.push(choices.slice(i, i + 2))
    }

    return (
      <div
        ref={ref}
        className={cn(
          "flex flex-col gap-4 items-center justify-center",
          "bg-gray-100 rounded-[0.3125rem] p-4",
          "w-full",
          className
        )}
      >
        {/* Message */}
        <p className="font-sf-pro text-[0.9375rem] text-black leading-normal w-full whitespace-pre-wrap">
          {message}
        </p>

        {/* Choices Grid (2 columns for both single and multiple modes) */}
        <div className="flex flex-col gap-4 w-full">
          {rows.map((row, rowIndex) => (
            <div key={rowIndex} className="flex gap-4 w-full">
              {row.map((choice) => (
                <label
                  key={choice.id}
                  className={cn(
                    "flex flex-1 gap-4 items-start cursor-pointer",
                    "min-w-0" // Allow flex items to shrink
                  )}
                >
                  {mode === 'single' ? (
                    // Single-choice mode: Radio button
                    <div className="relative shrink-0 size-5 mt-0.5">
                      <input
                        type="radio"
                        name="choice"
                        value={choice.id}
                        checked={selectedId === choice.id}
                        onChange={() => handleRadioChange(choice.id)}
                        className="sr-only"
                      />
                      <div
                        className={cn(
                          "size-5 rounded-full border-2 transition-colors",
                          "flex items-center justify-center",
                          selectedId === choice.id
                            ? "bg-black border-black"
                            : "bg-white border-gray-400"
                        )}
                      >
                        {selectedId === choice.id && (
                          <div className="size-2.5 rounded-full bg-white" />
                        )}
                      </div>
                    </div>
                  ) : (
                    // Multiple-choice mode: Checkbox
                    <div className="relative shrink-0 size-4">
                      <input
                        type="checkbox"
                        checked={selectedIds.has(choice.id)}
                        onChange={() => handleCheckboxChange(choice.id)}
                        className="sr-only"
                      />
                      <div
                        className={cn(
                          "size-4 rounded-sm border-2 transition-colors",
                          "flex items-center justify-center",
                          selectedIds.has(choice.id)
                            ? "bg-black border-black"
                            : "bg-white border-gray-400"
                        )}
                      >
                        {selectedIds.has(choice.id) && (
                          <svg
                            className="size-3 text-white"
                            fill="none"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth="2"
                            viewBox="0 0 24 24"
                            stroke="currentColor"
                          >
                            <path d="M5 13l4 4L19 7" />
                          </svg>
                        )}
                      </div>
                    </div>
                  )}

                  {/* Label */}
                  <span className="font-sf-pro text-[0.9375rem] text-black leading-normal">
                    {choice.label}
                  </span>
                </label>
              ))}
              {/* Add empty space if row has only one item */}
              {row.length === 1 && <div className="flex-1" />}
            </div>
          ))}
        </div>

        {/* Buttons */}
        <div className="flex gap-4 w-full">
          {/* Cancel Button */}
          <button
            onClick={onCancel}
            className={cn(
              "flex-1 flex items-center justify-center",
              "bg-white rounded-[0.375rem]",
              "px-4 py-2 h-[2.3556rem]",
              "font-montserrat font-bold text-[0.9375rem] text-black text-center uppercase",
              "hover:bg-gray-50 active:bg-gray-100",
              "transition-colors"
            )}
          >
            {cancelText}
          </button>

          {/* Confirm Button */}
          <button
            onClick={handleConfirm}
            className={cn(
              "flex-1 flex items-center justify-center",
              "bg-gray-900 rounded-[0.375rem]",
              "px-4 py-2 h-[2.3556rem]",
              "font-montserrat font-bold text-[0.9375rem] text-white text-center uppercase",
              "hover:bg-gray-700 active:bg-gray-900",
              "transition-colors"
            )}
          >
            {confirmText}
          </button>
        </div>
      </div>
    )
  }
)

MultiChoiceModal.displayName = "MultiChoiceModal"
